import React, {useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useDispatch, useSelector} from "react-redux";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import CardActions from "@material-ui/core/CardActions";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import TextField from "@material-ui/core/TextField";
import DialogActions from "@material-ui/core/DialogActions";
import {connect, setAdminAuthenticated, setFirmwareVersion, transceive} from "./actions";
import {byteToHexString, hexStringToString} from "./util";
import {useSnackbar} from "notistack";
import {Switch} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  card: {
    marginLeft: "10%",
    marginRight: "10%",
    marginTop: "30px",
  },
  buttonGroup: {
    marginLeft: "20px"
  }
}));

/* eslint-disable no-throw-literal */
export default function Overview() {
  const classes = useStyles();
  const device = useSelector(state => state.device);
  const model = useSelector(state => state.model);
  const firmwareVersion = useSelector(state => state.firmwareVersion);
  const dispatch = useDispatch();
  const authenticated = useSelector(state => state.adminAuthenticated);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [chPinDialogOpen, setChPinDialogOpen] = useState(false);
  const [changeCacheTimeDialogOpen, setChangeCacheTimeDialogOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [cacheTime, setCacheTime] = useState(0);
  const [flashSpace, setFlashSpace] = useState('unknown');
  const [state, setState] = useState({
    led: false,
    hotp: false,
    ndefReadonly: false,
    sigTouch: false,
    decTouch: false,
    autTouch: false,
    cacheTime: 0,
    ndefEnabled: false,
    webusbLandingEnabled: false
  });
  const {enqueueSnackbar} = useSnackbar();

  const onAuthenticate = useCallback(() => {
    setPinDialogOpen(true);
  }, []);

  const onChangePin = useCallback(() => {
    setChPinDialogOpen(true);
  }, []);

  const onChangeCacheTime = useCallback(() => {
    setChangeCacheTimeDialogOpen(true);
  }, []);

  const selectAdminApplet = useCallback(async () => {
    if (device === null) {
      if (!await dispatch(connect())) {
        throw 'Cannot connect to CanoKey';
      }
    }
    let res = await dispatch(transceive("00A4040005F000000000"));
    if (!res.endsWith("9000")) {
      throw 'Selecting admin applet failed';
    }
    if (firmwareVersion === '') {
      res = await dispatch(transceive("0031000000"));
      if (res.endsWith("9000")) {
        let version = hexStringToString(res.substring(0, res.length - 4));
        dispatch(setFirmwareVersion(version));
      }
    }
  }, [device, dispatch]);

  const adminTransceive = useCallback(async (capdu, success_msg, failed_msg, secret) => {
    try {
      let res = await dispatch(transceive(capdu, secret));
      if (res.endsWith("9000")) {
        enqueueSnackbar(success_msg, {variant: 'success'});
        return true;
      } else {
        enqueueSnackbar(failed_msg, {variant: 'error'});
      }
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
    return false;
  }, [dispatch, enqueueSnackbar]);

  const doAuthenticate = useCallback(async () => {
    setPinDialogOpen(false);
    try {
      let array = new TextEncoder().encode(pin);
      let len = new Uint8Array([array.length]);
      await selectAdminApplet();
      let res = await dispatch(transceive(`00200000${byteToHexString(len)}${byteToHexString(array)}`, true));
      if (res.startsWith("63C")) {
        let retry = parseInt(res.substr(3, 1), 16);
        enqueueSnackbar(`PIN verification failed, ${retry} retires left`, {variant: 'error'});
      } else if (res.endsWith("9000")) {
        dispatch(setAdminAuthenticated(true));
        enqueueSnackbar('PIN verification success', {variant: 'success'});
        // read flash usage
        res = await dispatch(transceive("0041000000"));
        if (res.endsWith("9000")) {
          let used = parseInt(res.substring(0, 2), 16);
          let total = parseInt(res.substring(2, 4), 16);
          setFlashSpace(`${used} KiB / ${total} KiB`);
        }
        // read configs
        res = await dispatch(transceive("0042000000"));
        if (res.endsWith("9000")) {
          if (firmwareVersion.toString() < "1.5") {
            let ledOn = res.substring(0, 2) === "01";
            let hotpOn = res.substring(2, 4) === "01";
            let ndefReadonly = res.substring(4, 6) === "01";
            let sigTouch = res.substring(6, 8) === "01";
            let decTouch = res.substring(8, 10) === "01";
            let autTouch = res.substring(10, 12) === "01";
            let cacheTime = parseInt(res.substring(12, 14), 16);
            setState({
              led: ledOn,
              hotp: hotpOn,
              ndefReadonly: ndefReadonly,
              sigTouch: sigTouch,
              decTouch: decTouch,
              autTouch: autTouch,
              cacheTime: cacheTime
            });
          } else {
            let ledOn = res.substring(0, 2) === "01";
            let hotpOn = res.substring(2, 4) === "01";
            let ndefReadonly = res.substring(4, 6) === "01";
            let ndefEnabled = res.substring(6, 8) === "01";
            let webusbLandingEnabled = res.substring(8, 10) === "01";
            setState({
              led: ledOn,
              hotp: hotpOn,
              ndefReadonly: ndefReadonly,
              ndefEnabled: ndefEnabled,
              webusbLandingEnabled: webusbLandingEnabled
            });
          }
        }
      } else {
        enqueueSnackbar('PIN verification failed', {variant: 'error'});
      }
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
  }, [pin, dispatch, enqueueSnackbar, selectAdminApplet]);

  const doChangePin = useCallback(async () => {
    setChPinDialogOpen(false);
    try {
      let array = new TextEncoder().encode(pin);
      let len = new Uint8Array([array.length]);
      let res = await dispatch(transceive(`00210000${byteToHexString(len)}${byteToHexString(array)}`, true));
      if (res.endsWith("9000")) {
        dispatch(setAdminAuthenticated(true));
        enqueueSnackbar('PIN changed', {variant: 'success'});
      } else {
        enqueueSnackbar('Change PIN failed', {variant: 'error'});
      }
    } catch (err) {
      enqueueSnackbar(err.toString(), {variant: 'error'});
    }
  }, [pin, dispatch, enqueueSnackbar]);

  const doChangeCacheTime = useCallback(async () => {
    setChangeCacheTimeDialogOpen(false);
    await adminTransceive(`000903${byteToHexString([cacheTime])}`, "Cache time changed", "Change cache time failed");
    setState(oldState => ({...oldState, cacheTime: cacheTime}));
  }, [cacheTime, adminTransceive]);

  const onKeyPress = useCallback(async (e) => {
    if (e.key === 'Enter') {
      await doAuthenticate();
    }
  }, [doAuthenticate]);

  const onKeyPressChPin = useCallback(async (e) => {
    if (e.key === 'Enter') {
      await doChangePin();
    }
  }, [doChangePin]);

  const onKeyPressChangeCacheTime = useCallback(async (e) => {
    if (e.key === 'Enter') {
      await doChangeCacheTime();
    }
  }, [doChangeCacheTime]);

  const setLedStatus = useCallback(async (e) => {
    let ledOn = e.target.checked;
    if (ledOn) {
      await adminTransceive("00400101", "LED is on", "Set LED status failed");
    } else {
      await adminTransceive("00400100", "LED is off", "Set LED status failed");
    }
    setState(oldState => ({...oldState, led: ledOn}));
  }, [adminTransceive]);

  const setHotpStatus = useCallback(async (e) => {
    let hotpOn = e.target.checked;
    if (hotpOn) {
      await adminTransceive("00400301", "HOTP on touch is on", "Set HOTP status failed");
    } else {
      await adminTransceive("00400300", "HOTP on touch is off", "Set HOTP status failed");
    }
    setState(oldState => ({...oldState, hotp: hotpOn}));
  }, [adminTransceive]);

  const setNDEFReadonly = useCallback(async (e) => {
    let readonly = e.target.checked;
    if (readonly) {
      await adminTransceive("00080100", "NDEF is RO", "Set NDEF status failed");
    } else {
      await adminTransceive("00080000", "NDEF is RW", "Set NDEF status failed");
    }
    setState(oldState => ({...oldState, ndefReadonly: readonly}));
  }, [adminTransceive]);

  const setNDEFStatus = useCallback(async (e) => {
    let enabled = e.target.checked;
    if (enabled) {
      await adminTransceive("00400401", "NDEF is enabled", "Set NDEF status failed");
    } else {
      await adminTransceive("00400400", "NDEF is disabled", "Set NDEF status failed");
    }
    setState(oldState => ({...oldState, ndefEnabled: enabled}));
  }, [adminTransceive]);

  const setWebUSBLandingStatus = useCallback(async (e) => {
    let enabled = e.target.checked;
    if (enabled) {
      await adminTransceive("00400501", "WebUSB prompt is enabled", "Set WebUSB prompt failed");
    } else {
      await adminTransceive("00400500", "WebUSB prompt disabled", "Set WebUSB prompt failed");
    }
    setState(oldState => ({...oldState, webusbLandingEnabled: enabled}));
  }, [adminTransceive]);

  const resetOpenPGP = useCallback(async () => {
    await adminTransceive("00030000", "Reset OpenPGP done", "Reset OpenPGP failed");
  }, [adminTransceive]);

  const resetPIV = useCallback(async () => {
    await adminTransceive("00040000", "Reset PIV done", "Reset PIV failed");
  }, [adminTransceive]);

  const resetOATH = useCallback(async () => {
    await adminTransceive("00050000", "Reset OATH done", "Reset OATH failed");
  }, [adminTransceive]);

  const resetNDEF = useCallback(async () => {
    await adminTransceive("00070000", "Reset NDEF done", "Reset NDEF failed");
  }, [adminTransceive]);

  const enterDFU = useCallback(async () => {
    await adminTransceive("00FF2222", "Enter DFU: Unexpected success",
      "Enter DFU: WebUSB disconnected, device should be in DFU now");
  }, [adminTransceive]);

  const setSigTouch = useCallback(async (e) => {
    let touch = e.target.checked;
    if (touch) {
      await adminTransceive("00090001", "Set SIG touch policy REQUIRED", "Set SIG touch policy failed");
    } else {
      await adminTransceive("00090000", "Set SIG touch policy NO", "Set SIG touch policy failed");
    }
    setState(oldState => ({...oldState, sigTouch: touch}));
  }, [adminTransceive]);

  const setDecTouch = useCallback(async (e) => {
    let touch = e.target.checked;
    if (touch) {
      await adminTransceive("00090101", "Set DEC touch policy REQUIRED", "Set DEC touch policy failed");
    } else {
      await adminTransceive("00090100", "Set DEC touch policy NO", "Set DEC touch policy failed");
    }
    setState(oldState => ({...oldState, decTouch: touch}));
  }, [adminTransceive]);

  const setAutTouch = useCallback(async (e) => {
    let touch = e.target.checked;
    if (touch) {
      await adminTransceive("00090201", "Set AUT touch policy REQUIRED", "Set AUT touch policy failed");
    } else {
      await adminTransceive("00090200", "Set AUT touch policy NO", "Set AUT touch policy failed");
    }
    setState(oldState => ({...oldState, autTouch: touch}));
  }, [adminTransceive]);

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h3">
            管理小程序
          </Typography>
          <Typography>
            已认证： {authenticated ? '是' : '否'}
          </Typography>
          <Typography>
            存储用量：{flashSpace}
          </Typography>
        </CardContent>
        <CardActions>
          <Button onClick={onAuthenticate} variant="contained">
            认证
          </Button>
        </CardActions>
      </Card>
      {
        authenticated ?
          <div>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h3">
                  配置
                </Typography>
                <Typography variant="h6">
                  LED:
                  <Switch checked={state.led} onChange={setLedStatus}/>
                </Typography>
                <Typography variant="h6">
                  触摸 HOTP：
                  <Switch checked={state.hotp} onChange={setHotpStatus}/>
                </Typography>
                <Typography variant="h6">
                  NDEF 只读：
                  <Switch checked={state.ndefReadonly} onChange={setNDEFReadonly}/>
                </Typography>
                {
                  firmwareVersion.toString() < "1.5" ?
                    <div>
                      <Typography variant="h6">
                      OpenPGP SIG 触摸政策：
                        <Switch checked={state.sigTouch} onChange={setSigTouch}/>
                      </Typography>
                      <Typography variant="h6">
                      OpenPGP DEC 触摸政策：
                      <Switch checked={state.decTouch} onChange={setDecTouch}/>
                      </Typography>
                      <Typography variant="h6">
                      OpenPGP AUT 触摸政策：
                      <Switch checked={state.autTouch} onChange={setAutTouch}/>
                      </Typography>
                      <Typography variant="h6">
                      OpenPGP 触摸缓存时间： {state.cacheTime}
                      <Button variant="contained" onClick={onChangeCacheTime}> 改变 </Button>
                      </Typography>
                    </div> :
                    <div>
                      <Typography variant="h6">
                        启用 NDEF：
                        <Switch checked={state.ndefEnabled} onChange={setNDEFStatus}/>
                      </Typography>
                      <Typography variant="h6">
                        启用 WebUSB 提示：
                        <Switch checked={state.webusbLandingEnabled} onChange={setWebUSBLandingStatus}/>
                      </Typography>
                    </div>
                }
              </CardContent>
              <CardActions>
                <Button variant="contained" onClick={onChangePin}> 更改 PIN </Button>
                {
                  model.toString().indexOf("STM32") >= 0 ?
                    <div>
                      <Button variant="contained" onClick={enterDFU}>进入 DFU（仅限开发用途）</Button>
                      <Button variant="contained" onClick={() => window.location = 'https://dfu.canokeys.org/'}>
                        转到 Web DFU 实用程序
                      </Button>
                    </div>
                    : null
                }

              </CardActions>
            </Card>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h3">
                  重置小程序
                </Typography>
              </CardContent>
              <CardActions>
                <Button onClick={resetOpenPGP} variant="contained">重置 OpenPGP</Button>
                 <Button onClick={resetPIV} variant="contained">重置 PIV</Button>
                 <Button onClick={resetOATH} variant="contained">重置 OATH</Button>
                 <Button onClick={resetNDEF} variant="contained">重置 NDEF</Button>
              </CardActions>
            </Card>
          </div>
          : null
      }
      <Dialog open={pinDialogOpen} onClose={() => setPinDialogOpen(false)}>
        <DialogTitle>输入 PIN 以验证管理员小程序</DialogTitle>
        <DialogContent>
          <DialogContentText>
            在下面输入 PIN。 默认值为 123456。请注意 PIN 重试次数。 这不会存储在浏览器中。
          </DialogContentText>
          <TextField
            type="password"
            autoFocus
            fullWidth
            onKeyPress={onKeyPress}
            onChange={(e) => setPin(e.target.value)}/>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={doAuthenticate}>
            认证
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={chPinDialogOpen} onClose={() => setChPinDialogOpen(false)}>
        <DialogTitle>输入管理小程序的新 PIN</DialogTitle>
        <DialogContent>
          <DialogContentText>
            输入管理小程序的新 PIN。
          </DialogContentText>
          <TextField
            type="password"
            autoFocus
            fullWidth
            onKeyPress={onKeyPressChPin}
            onChange={(e) => setPin(e.target.value)}/>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={doChangePin}>
            更换 Pin
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={changeCacheTimeDialogOpen} onClose={() => setChangeCacheTimeDialogOpen(false)}>
        <DialogTitle>输入新的缓存时间</DialogTitle>
        <DialogContent>
          <DialogContentText>
            为 OpenPGP 触摸 输入新的缓存时间（以秒为单位）：0 ~ 255。
          </DialogContentText>
          <TextField
            type="number"
            autoFocus
            fullWidth
            InputProps={{ inputProps: { min: 0, max: 255 } }}
            onKeyPress={onKeyPressChangeCacheTime}
            onChange={(e) => setCacheTime(parseInt(e.target.value))}/>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={doChangeCacheTime}>
            确认
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
